import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import type { InviteResult, PoolRow } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { Field } from '../../../components/Field';
import { Input, Textarea } from '../../../components/Input';
import { Select } from '../../../components/Select';
import { errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { useJobs } from '../../jobs/api/jobs-api';
import { POOL_MATCH_KEY, POOLS_KEY, CANDIDATE_SEARCH_KEY, poolsApi, usePoolOptions } from '../api/pools-api';
import styles from '../Pools.module.css';

/**
 * The three small forms S25 opens next to the button that asked for them (CLAUDE.md §5):
 * a pool's name and description, "add to a pool", and "invite to a job".
 */

export function usePoolRefresh(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: POOLS_KEY }),
      queryClient.invalidateQueries({ queryKey: POOL_MATCH_KEY }),
      queryClient.invalidateQueries({ queryKey: CANDIDATE_SEARCH_KEY }),
      queryClient.invalidateQueries({ queryKey: ['candidates'] }),
      queryClient.invalidateQueries({ queryKey: ['applications'] }),
    ]);
  };
}

/** F14.01: name, what it is for, and — when editing — taking it on yourself. */
export function PoolForm({ pool, onDone, onCancel }: { readonly pool: PoolRow | null; readonly onDone: (saved: PoolRow) => void; readonly onCancel: () => void }): ReactNode {
  const show = useToasts((state) => state.show);
  const refresh = usePoolRefresh();
  const [name, setName] = useState(pool?.name ?? '');
  const [description, setDescription] = useState(pool?.description ?? '');
  const [takeOwnership, setTakeOwnership] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const save = async (): Promise<void> => {
    if (name.trim().length < 2) {
      setError('Give the pool a name of at least 2 characters.');
      return;
    }
    setIsSaving(true);
    try {
      const saved = pool
        ? await poolsApi.update(pool.id, { name: name.trim(), description, ownerUserId: takeOwnership ? undefined : pool.ownerUserId, version: pool.version })
        : await poolsApi.create({ name: name.trim(), description });
      await refresh();
      show({ tone: 'success', message: pool ? 'Pool saved' : `${saved.name} created` });
      onDone(saved);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          void save();
        }
      }}
    >
      <Field label="Name" isRequired error={error ?? undefined}>
        {({ inputId, describedBy, isInvalid }) => (
          <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} value={name} maxLength={80} autoFocus placeholder="Finance — strong finalists" onChange={(event) => setName(event.target.value)} />
        )}
      </Field>
      <Field label="What it is for" isOptional>
        {({ inputId }) => <Textarea id={inputId} value={description} maxLength={400} placeholder="People we would call first for a senior finance role" onChange={(event) => setDescription(event.target.value)} />}
      </Field>
      {pool && !pool.isSystem && (
        <Checkbox checked={takeOwnership} onChange={setTakeOwnership} label="Make me the owner — I hear when consent is running out" />
      )}
      <div className={styles.formActions}>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" type="submit" isPending={isSaving}>
          {pool ? 'Save' : 'Create pool'}
        </Button>
      </div>
    </form>
  );
}

/** F14.02 from anywhere: choose the pool, say why. */
export function AddToPoolPanel({
  candidateIds,
  who,
  sourceApplicationId,
  onDone,
  onCancel,
}: {
  readonly candidateIds: readonly string[];
  readonly who: string;
  readonly sourceApplicationId?: string | null;
  readonly onDone: () => void;
  readonly onCancel: () => void;
}): ReactNode {
  const show = useToasts((state) => state.show);
  const refresh = usePoolRefresh();
  const options = usePoolOptions();
  const [poolId, setPoolId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const add = async (): Promise<void> => {
    if (!poolId) return;
    setIsSaving(true);
    try {
      const result = await poolsApi.addMembers(poolId, { candidateIds: [...candidateIds], note, sourceApplicationId: sourceApplicationId ?? null });
      await refresh();
      const poolName = options.data?.find((option) => option.id === poolId)?.name ?? 'the pool';
      show({
        tone: 'success',
        message: result.added === 0 ? `Already in ${poolName}` : `Added to ${poolName}`,
        detail: result.alreadyIn > 0 && result.added > 0 ? `${result.alreadyIn} were already there.` : undefined,
      });
      onDone();
    } catch (cause) {
      show({ tone: 'danger', message: "They weren't added", detail: errorMessage(cause) });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.form}>
      <p className={styles.meta}>Add {who} to a talent pool. Their applications keep their outcomes.</p>
      <Field label="Pool" isRequired>
        {({ inputId }) => (
          <Select
            id={inputId}
            label="Pool"
            value={poolId}
            placeholder={options.isPending ? 'Loading pools…' : 'Choose a pool'}
            options={(options.data ?? []).map((option) => ({ value: option.id, label: option.name, description: `${option.memberCount} ${option.memberCount === 1 ? 'person' : 'people'}` }))}
            onChange={setPoolId}
          />
        )}
      </Field>
      <Field label="Why" isOptional hint="Shown beside them in the pool.">
        {({ inputId }) => <Textarea id={inputId} value={note} maxLength={500} placeholder="Strong interview, salary too high this time" onChange={(event) => setNote(event.target.value)} />}
      </Field>
      <div className={styles.formActions}>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" isPending={isSaving} disabled={!poolId} onClick={() => void add()}>
          Add to pool
        </Button>
      </div>
    </div>
  );
}

/** F14.05 from a pool: pick an open job; the server checks each person against it. */
export function InviteToJobPanel({ candidateIds, onDone, onCancel }: { readonly candidateIds: readonly string[]; readonly onDone: (result: InviteResult) => void; readonly onCancel: () => void }): ReactNode {
  const show = useToasts((state) => state.show);
  const refresh = usePoolRefresh();
  const jobs = useJobs({ page: 1, pageSize: 200, status: 'open' });
  const [jobId, setJobId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  const invite = async (): Promise<void> => {
    if (!jobId) return;
    setIsSending(true);
    try {
      const result = await poolsApi.invite({ jobId, candidateIds: [...candidateIds] });
      await refresh();
      announceInvite(show, result);
      onDone(result);
    } catch (cause) {
      show({ tone: 'danger', message: 'Nobody was invited', detail: errorMessage(cause) });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className={styles.form}>
      <p className={styles.meta}>Each person gets an application on the job and an email about it. Anyone below the job's pool threshold, or excluded, is skipped and you are told why.</p>
      <Field label="Job" isRequired>
        {({ inputId }) => (
          <Select
            id={inputId}
            label="Job"
            value={jobId}
            placeholder={jobs.isPending ? 'Loading open jobs…' : 'Choose an open job'}
            options={(jobs.data?.rows ?? []).map((job) => ({ value: job.id, label: job.publicTitle ?? job.title, description: `${job.reference} · ${job.ventureName}` }))}
            onChange={setJobId}
          />
        )}
      </Field>
      <div className={styles.formActions}>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" isPending={isSending} disabled={!jobId} onClick={() => void invite()}>
          Invite {candidateIds.length === 1 ? '' : candidateIds.length}
        </Button>
      </div>
    </div>
  );
}

type Show = ReturnType<typeof useToasts.getState>['show'];

/** One toast that says who went and who did not, and why. */
export function announceInvite(show: Show, result: InviteResult): void {
  const invited = result.invited.length;
  const skipped = result.skipped;
  if (invited === 0) {
    show({ tone: 'warning', message: 'Nobody was invited', detail: skipped.map((row) => `${row.name}: ${row.reason}`).join(' ') });
    return;
  }
  show({
    tone: 'success',
    message: invited === 1 ? 'Invited — application made and email sent' : `${invited} invited — applications made and emails sent`,
    detail: skipped.length > 0 ? `Skipped: ${skipped.map((row) => `${row.name} (${row.reason})`).join(', ')}` : undefined,
  });
}
